import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { BookingModel, BookingService, LayoutService, NavigationService } from 'app-components';
import moment from 'moment';
import { AppIntegrationService, AppSettingsService, Coordinates } from 't4-app-integration';
import { LocationInfo, OpenHours, PageBase, TypeCategoryInfo } from 't4core';
import { Country, getCountryDataByName } from '../../../../lib/Models/Country';


interface ExtendedTypeCategoryInfo extends TypeCategoryInfo {
  disabled?: boolean;
}

@Component({
  selector: 'web-landing-page-component',
  templateUrl: './web-landing-page.html',
  styleUrls: ['./web-landing-page.css'],
  standalone: false,
})
export class WebLandingPageComponent extends PageBase {

  // Selection choices
  public selectedLocation: LocationInfo = null;
  public selectedType: ExtendedTypeCategoryInfo = null;

  public pickupDate: Date = new Date();
  public returnDate: Date = new Date();
  public pickupTime: Date = this.roundToNext30Minutes(new Date());
  public returnTime: Date = this.roundToNext30Minutes(new Date(new Date().setHours(new Date().getHours() + this.Settings.clientSettings.Bookings.DefaultBookingLength)));
  public bookOverNight: boolean = false;

  public minDate: Date = new Date();

  //Data
  public location: string = "";
  public locations: LocationInfo[] = [];
  public filteredLocationsList: LocationInfo[] = [];
  public hoveredLocation: LocationInfo = null;
  public typeCategoryList: ExtendedTypeCategoryInfo[] = [];
  public filterdCategoryList: ExtendedTypeCategoryInfo[] = [];
  public filterCountry: Country;
  public filteredLocations: LocationInfo[] = [...this.filteredLocationsList];
  public searchedOnLocation: boolean = false;
  public allCategories: ExtendedTypeCategoryInfo = { Id: '0', Name: "All Categories", disabled: false, ContextId: this.appSettings.contextId, Description: "All Categories", SortOrder: 0, Content: [], ImageUri: "", Countries: [] };
  public loading: boolean = false;

  //Round the starttime to the closest 30min.
  public pickupStartLimit: string = moment().add(30 - (moment().minute() % 30), 'minutes').startOf('minute').format('HH:mm');
  public returnStartLimit: string = moment(this.pickupStartLimit, 'HH:mm').add(1, 'hour').format('HH:mm');

  public pickupEndLimit: string = "";
  public returnEndLimit: string = "";

  public backgroundImageUri = "url(/Styles/Use4Free/Images/bg/Default.jpg)";

  //Booking
  public bookingState: BookingModel;

  @ViewChild('categoriesSelect') categoriesSelect!: MatSelect;
  @ViewChild('dropdownContainer') dropdownContainer!: ElementRef<HTMLDivElement>;

  //Google
  @ViewChild('search') private searchElement: ElementRef;
  private searchBox: google.maps.places.Autocomplete;
  private listener: google.maps.MapsEventListener;

  constructor(
    private dateAdapter: DateAdapter<Date>,
    private navigator: NavigationService,
    el: ElementRef,
    private appService: AppIntegrationService,
    private cdr: ChangeDetectorRef,
    private BookingDataSerivce: BookingService,
    public layout: LayoutService,
    public appSettings: AppSettingsService,
    private bookingSvc: BookingService) {
    super(el);
    this.dateAdapter.setLocale('en-GB');
  }

  async ngOnInit() {
    this.loading = true;

    await this.BookingDataSerivce.startSession();
    this.bookingState = this.BookingDataSerivce.getBookingState();
    this.locations = await this.BookingDataSerivce.getLocations();
    this.filteredLocationsList = this.locations;
    await this.getFamilyTypes();

    if (this.appSettings.getParam("pickupLocationId")) {
      var location = this.filteredLocationsList.find((x) => x.Id.toString() == this.appSettings.getParam("pickupLocationId")) ?? null;
      if (location) this.selectLocation(location)
    }

    this.initGoogle();
    this.loading = false;
  }

  selectLocation(loc: LocationInfo) {
    this.location = loc.Name;
    this.selectedLocation = loc;
    this.updateViableTypes(loc);

    this.getOpenHours(loc);

    //Moves the focus to the RentalObject, after we have selected a Location.
    setTimeout(() => {
      this.categoriesSelect.focus();
    }, 50);

    this.hoveredIndex = 0;
  }

  public clearLocationInput() {
    this.location = "";
    this.searchElement.nativeElement.value = "";
    this.searchedOnLocation = false;
    this.selectedLocation = null;
    this.updateViableTypes(null);
    this.setHoveredLocation(null);
  }

  selectCategory(category: TypeCategoryInfo) {
    this.selectedType = category;
  }

  filterLocations() {
    this.filteredLocations = this.filteredLocationsList.filter(loc =>
      loc.Name.toLowerCase().includes(this.location.toLowerCase())
    );

    if (!this.location || this.location.trim() === "") {
      this.clearLocationInput();
    }
  }

  public toggleOverNight() {
    this.bookOverNight = true;
    this.returnDate = new Date(this.pickupDate);
    this.returnDate.setDate(this.returnDate.getDate() + 1);

    if (this.selectedLocation)
      this.getOpenHours(this.selectedLocation);
  }

  public goToMap() {
    this.navigator.executeCommand("Map");
  }

  public setHoveredLocation(loc: LocationInfo) {
    this.hoveredLocation = loc;
  }

  public async getFamilyTypes() {
    this.typeCategoryList = await this.BookingDataSerivce.getCategories();
    //Add the Defauly / dummy object to the list.
    this.typeCategoryList = [this.allCategories, ...this.typeCategoryList];
    //Pre select, 'all Categories'
    this.selectedType = this.typeCategoryList[0];

    var country = this.appSettings.getAnticipatedUserCountry();
    if (country) {
      this.filterCountry = getCountryDataByName(country);
    } else {
      this.filterCountry = null;
    }

    if (this.filterCountry) {
      this.filterdCategoryList = this.typeCategoryList.filter(x => {
        return x.Countries == undefined || x.Countries.length == 0 || x.Countries.indexOf(this.filterCountry.shortName.toUpperCase()) !== -1
      })
    } else {
      this.filterdCategoryList = this.typeCategoryList;
    }
  }

  public async updateViableTypes(loc: LocationInfo) {
    //If no Location is provided ,we want all types to be enabled.
    if (loc == null) {
      this.filterdCategoryList = this.typeCategoryList.map(category => ({
        ...category,
        disabled: false,
      }));

      if (this.selectedType) {
        this.selectedType = this.filterdCategoryList.find(x => x.Id === this.selectedType.Id);
      }

      return;
    }

    var locationCategories = await this.BookingDataSerivce.getFamiliesAtLocation(loc) as ExtendedTypeCategoryInfo[];
    const availableCategoryIds = new Set(locationCategories.map(c => c.Id));

    // Update the filter list, setting `disabled` where needed.
    this.filterdCategoryList = this.typeCategoryList.map(category => ({
      ...category,
      disabled: !availableCategoryIds.has(category.Id) && category.Id != "0",  //Not marking the 'All categories' as disabled.
    }));

    this.selectedType = this.filterdCategoryList.find(location => !location.disabled);
  }


  public async searchOnData() {
    //Save selected Type, as Long as not set to All (Id 0)
    if (this.selectedType && this.selectedType.Id != "0") this.bookingState.objectCategory = this.selectedType;

    //Save selected Location
    if (this.selectedLocation) this.BookingDataSerivce.setPickupLocation(this.selectedLocation);

    //saves the times to a moment.Moment object.
    if (this.pickupDate && this.pickupTime) {
      var _pickupTime = await this.combineDateAndTime(this.pickupDate, this.pickupTime);
      this.bookingState.booking.PickupTime = _pickupTime.clone();
    }
    if (this.returnDate && this.returnTime) {
      var _returnTime = await this.combineDateAndTime(this.returnDate, this.returnTime);
      this.bookingState.booking.ReturnTime = _returnTime.clone();
    }

    this.BookingDataSerivce.saveState(this.bookingState);

    while (this.bookingSvc.loading)
      await new Promise(resolve => setTimeout(resolve, 100));

    if (!this.selectedLocation) this.goToMap();
    else
      this.navigator.executeCommand("Search");
  }



  //<< ======== Search & Input ======== >>
  private async initGoogle() {
    const { } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;

    this.searchBox = new google.maps.places.Autocomplete(this.searchElement.nativeElement, {
      componentRestrictions: { country: ['NO', 'SE', 'DK', 'DE', 'IS'] },
      types: ['locality', 'neighborhood', 'sublocality'],
      fields: ['geometry', 'formatted_address']
    });

    this.searchElement.nativeElement.focus();

    this.listener = this.searchBox.addListener("place_changed", () => {
      let place: google.maps.places.PlaceResult = this.searchBox.getPlace();
      if (place.geometry) {
        this.searchedOnLocation = true;
        this.selectedLocation = null;
        this.location = place.formatted_address;
        this.searchPlace({ Latitude: place.geometry.location.lat(), Longitude: place.geometry.location.lng() });

        setTimeout(() => {
          this.dropdownContainer.nativeElement.focus();
        }, 100);
      }
    });
  }

  public async searchPlace(coords: Coordinates) {
    this.filteredLocationsList = await this.bookingSvc.getClosestLocations(coords.Latitude, coords.Longitude, this.locations);
    this.hoveredLocation = this.filteredLocationsList[0];
  }

  private hoveredIndex: number = 0;

  handleKeyDown(event: KeyboardEvent) {
    if (!this.filteredLocationsList.length) return;

    if (event.key === 'ArrowDown') {
      this.hoveredIndex = (this.hoveredIndex + 1) % this.filteredLocationsList.length;
      this.hoveredLocation = this.filteredLocationsList[this.hoveredIndex];
      this.scrollHoveredIntoView();
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      this.hoveredIndex = (this.hoveredIndex - 1 + this.filteredLocationsList.length) % this.filteredLocationsList.length;
      this.hoveredLocation = this.filteredLocationsList[this.hoveredIndex];
      this.scrollHoveredIntoView();
      event.preventDefault();
    } else if (event.key === 'Enter') {
      if (this.hoveredLocation) {
        this.selectLocation(this.hoveredLocation);
      }
    }
  }

  scrollHoveredIntoView() {
    const elements = document.querySelectorAll('.locations-list mat-option');
    if (elements[this.hoveredIndex]) {
      (elements[this.hoveredIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }


  // << ======== Time & Date ======== >>
  public get isBookingTimeValid(): boolean {
    if (!this.pickupDate || !this.returnDate || !this.pickupTime || !this.returnTime) {
      return false;
    }

    const pickupMoment = moment(this.pickupDate).set({
      hour: this.pickupTime.getHours(),
      minute: this.pickupTime.getMinutes(),
      second: 0,
      millisecond: 0
    });

    const returnMoment = moment(this.returnDate).set({
      hour: this.returnTime.getHours(),
      minute: this.returnTime.getMinutes(),
      second: 0,
      millisecond: 0
    });

    return returnMoment.isAfter(pickupMoment);
  }


  private enforceTimeBetweenLimits(date: Date, time: Date, minLimit: string, maxLimit: string): Date {
    const timeMoment = moment(time);

    const [minHour, minMinute] = minLimit.split(':').map(Number);
    const minMoment = moment(date).set({ hour: minHour, minute: minMinute, second: 0, millisecond: 0 });

    const [maxHour, maxMinute] = maxLimit.split(':').map(Number);
    const maxMoment = moment(date).set({ hour: maxHour, minute: maxMinute, second: 0, millisecond: 0 });

    if (timeMoment.isBefore(minMoment)) return minMoment.toDate();
    if (timeMoment.isAfter(maxMoment)) return maxMoment.toDate();

    return time;
  }


  async combineDateAndTime(date: Date, time: Date) {
    return moment(date).set({
      hour: time.getHours(),
      minute: time.getMinutes(),
      second: 0,
    });
  }

  private roundToNext30Minutes(date: Date): Date {
    const minutes = date.getMinutes();
    const remainder = 30 - (minutes % 30);
    date.setMinutes(minutes + remainder);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  private roundToNearest30ToString(time: moment.Moment): string {
    const minutes = time.minutes();
    const roundedMinutes = minutes % 30 === 0 ? minutes : minutes + (30 - (minutes % 30));
    return time.minutes(roundedMinutes).format("HH:mm");
  }

               
  private removeAddedTimeZone(val: string | moment.Moment): moment.Moment {
    if (typeof val === 'string') { 
      const m = moment.utc(val);
      return m.subtract(moment().utcOffset(), 'minutes');
    }

    if (moment.isMoment(val)) {
      return val.clone().subtract(val.utcOffset(), 'minutes');
    }

    return moment(val as any);
  }

  private async getOpenHours(location: LocationInfo) {
    const openHours: OpenHours[] =
      (await this.Api.get<OpenHours[]>('/Location/GetOpenHoursForPeriod', {
        locationId: location.Id,
        startDate: moment(this.pickupDate).format('YYYY-MM-DD'),
        endDate: moment(this.returnDate).add(1, 'day').format('YYYY-MM-DD')
      })).map(o => ({
        ...o,
        Date: this.removeAddedTimeZone(o.Date),
        Opens: this.removeAddedTimeZone(o.Opens),
        Closes: this.removeAddedTimeZone(o.Closes)
      }));


    const now = moment();
    const pickupDay = moment(this.pickupDate).startOf('day');
    const returnDay = moment(this.returnDate).startOf('day');

    const pickupHours = openHours.find(h => h.Date.isSame(pickupDay, 'day'));
    const returnHours = openHours.find(h => h.Date.isSame(returnDay, 'day'));

    if (pickupHours && pickupDay.isSame(now, 'day')) {
      const latestStart = pickupHours.Closes.clone().subtract(1, 'hour');
      if (now.isAfter(latestStart)) {

        const tomorrow = now.clone().add(1, 'day').startOf('day');
        this.pickupDate = this.returnDate = tomorrow.toDate();

        const tomHours = openHours.find(h => h.Date.isSame(tomorrow, 'day'));
        const open = tomHours?.Opens ?? tomorrow.clone().set({ hour: 8 });
        const rounded = open.clone().add(30 - (open.minute() % 30), 'minute');

        this.pickupTime = rounded.toDate(); 
        this.returnTime = rounded.clone().add(1, 'hour').toDate();

        /* limits for the pickers  */
        this.pickupStartLimit = this.roundToNearest30ToString(open);
        this.pickupEndLimit = this.roundToNearest30ToString(
          (tomHours?.Closes ?? open.clone().set({ hour: 22 }))
            .clone().subtract(1, 'hour'));

        this.returnStartLimit = moment(this.pickupStartLimit, 'HH:mm')
          .add(1, 'hour').format('HH:mm');
        this.returnEndLimit = this.roundToNearest30ToString(
          (tomHours?.Closes ?? open).clone());
        return;                             
      }
    }

    /* pick-up limits for selected day  */
    if (pickupHours) {
      if (pickupHours.Opens.isSame(pickupHours.Closes, 'day')) {
        const start = pickupDay.isSame(now, 'day') && pickupHours.Opens.isBefore(now)
          ? now.clone()
          : pickupHours.Opens.clone();
        this.pickupStartLimit = this.roundToNearest30ToString(start);
        this.pickupEndLimit = this.roundToNearest30ToString(
          pickupHours.Closes.clone().subtract(1, 'hour'));
      } else {
        const today = pickupDay.isSame(now, 'day');
        this.pickupStartLimit = today ? this.roundToNearest30ToString(now) : '00:30';
        this.pickupEndLimit = today ? '22:30' : '23:30';
      }
    }

    /* enforce pickupTime */
    this.pickupTime = this.enforceTimeBetweenLimits(
      this.pickupDate, this.pickupTime,
      this.pickupStartLimit, this.pickupEndLimit);

    /* return limits*/
    if (returnHours) {
      if (returnHours.Opens.isSame(returnHours.Closes, 'day')) {
        if (this.returnDate.getTime() === this.pickupDate.getTime()) {
          this.returnStartLimit = moment(this.pickupTime).add(1, 'hour').format('HH:mm');
        } else {
          this.returnStartLimit = moment(this.pickupStartLimit, 'HH:mm')
            .add(1, 'hour').format('HH:mm');
        }
        this.returnEndLimit = this.roundToNearest30ToString(returnHours.Closes);
      } else {
        if (this.returnDate.getTime() === this.pickupDate.getTime()) {
          this.returnStartLimit = moment(this.pickupTime).add(1, 'hour').format('HH:mm');
          this.returnEndLimit = '23:30';
        } else {
          this.returnStartLimit = '00:30';
          this.returnEndLimit = '23:30';
        }
      }
    }

    /* enforce returntime */
    this.returnTime = this.enforceTimeBetweenLimits(
      this.returnDate, this.returnTime,
      this.returnStartLimit, this.returnEndLimit);
  }




  public async updatePickupDate(newPickupDate: Date) {

    /* store new day and keep old duration */
    const originalDuration = this.returnDate.getTime() - this.pickupDate.getTime();
    this.pickupDate = newPickupDate;

    if (this.pickupDate.getTime() >= this.returnDate.getTime())
      this.returnDate = new Date(this.pickupDate.getTime() + originalDuration);

    if (!this.bookOverNight)
      this.returnDate = this.pickupDate;

    /* refresh limits with the selected location (if any) */
    if (this.selectedLocation) {
      await this.getOpenHours(this.selectedLocation);  
    } else {
      /* fallback limits when no station chosen */
      const today = moment().isSame(this.pickupDate, 'day');
      this.pickupStartLimit = today ? this.roundToNearest30ToString(moment()) : '00:30';
      this.pickupEndLimit = '23:30';
      this.returnStartLimit = moment(this.pickupStartLimit, 'HH:mm').add(1, 'hour').format('HH:mm');
      this.returnEndLimit = '23:30';
    }
  }



  public onPickupTimeChange(newPickupTime: Date) {
    var originalDuration = this.returnTime.getTime() - this.pickupTime.getTime();
    this.pickupTime = new Date(newPickupTime.getTime());

    if (this.pickupTime.getTime() > this.returnTime.getTime()) {
      var calculatedReturnTime = new Date(this.pickupTime.getTime() + originalDuration);

      var tomorrowMidnight = new Date(this.pickupTime);
      tomorrowMidnight.setHours(24, 0, 0, 0);

      if (calculatedReturnTime.getTime() > tomorrowMidnight.getTime()) {
        calculatedReturnTime = new Date(tomorrowMidnight);
      }
      if (this.pickupTime.getTime() == this.returnTime.getTime()) {
        this.returnTime = calculatedReturnTime;
      }
    }

    if (this.pickupDate.getDate() === this.returnDate.getDate()) {
      const pickupMoment = moment(this.pickupTime);
      const minimumReturnMoment = pickupMoment.clone().add(1, 'hour');

      if (moment(this.returnTime).isBefore(minimumReturnMoment)) {
        this.returnTime = minimumReturnMoment.toDate();
      }

      this.returnStartLimit = minimumReturnMoment.format("HH:mm");
    }
  }

  public onReturnTimeChange() {
    this.validateReturnTimeAfterDateChange();
    if (this.selectedLocation) {
      this.getOpenHours(this.selectedLocation);
    }
  }

  private validateReturnTimeAfterDateChange() {
    if (!this.pickupDate || !this.returnDate || !this.pickupTime || !this.returnTime) return;

    const pickupMoment = moment(this.pickupDate).set({
      hour: this.pickupTime.getHours(),
      minute: this.pickupTime.getMinutes(),
      second: 0,
      millisecond: 0
    });

    const returnMoment = moment(this.returnDate).set({
      hour: this.returnTime.getHours(),
      minute: this.returnTime.getMinutes(),
      second: 0,
      millisecond: 0
    });

    if (pickupMoment.isSame(returnMoment, 'day')) {
      if (!returnMoment.isAfter(pickupMoment)) {
        const minimumReturnMoment = pickupMoment.clone().add(1, 'hour');
        this.returnTime = minimumReturnMoment.toDate();
      }
    }
  }


}


